import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaChevronLeft, FaList, FaSearch, FaTh, FaTimes } from "react-icons/fa";
import { LuBuilding2, LuChevronRight, LuFactory, LuFileText, LuMapPin } from "react-icons/lu";
import HubHomeFooter from "../hub/HubHomeFooter";
import MarketplaceAdSection from "./MarketplaceAdSection";
import InfrastructureListingGridCard from "./InfrastructureListingGridCard";
import InfrastructureListingListRow from "./InfrastructureListingListRow";
import InfrastructureListingsFiltersPanel from "./InfrastructureListingsFiltersPanel";
import { buildMarketplaceInfraBrowseHref } from "../../constants/infrastructureLeasingBrowse";
import { buildMarketplaceModuleTabs, formatMarketplaceTabCount } from "../../constants/marketplaceModuleTabs";
import { listingAvailabilityFilterKey } from "../../utils/infrastructureLeasingSearchConfig";

const INFRA_SORT_OPTIONS = [
  { value: "newest", label: "Best Match" },
  { value: "price-asc", label: "Price: Low to High" },
  { value: "price-desc", label: "Price: High to Low" },
];

function formatStatCount(value) {
  return (Number(value) || 0).toLocaleString();
}

function infraListingDescription(name) {
  return `Browse verified infrastructure listings for ${name}. Compare lease terms, locations, availability, and connect with property owners and asset lessors.`;
}

function InfraListingHeroStats({ stats = {} }) {
  const pills = [
    {
      value: stats.listing_count ?? 0,
      label: "active listings",
      icon: LuBuilding2,
      iconClass: "explore-subcat-products-hero__stat-icon--suppliers",
    },
    {
      value: stats.location_count ?? 0,
      label: "locations",
      icon: LuMapPin,
      iconClass: "explore-subcat-products-hero__stat-icon--buyer-reqs",
    },
    {
      value: stats.available_count ?? 0,
      label: "available now",
      icon: LuFileText,
      iconClass: "explore-subcat-products-hero__stat-icon--auctions",
    },
    {
      value: stats.transaction_count ?? 0,
      label: "transaction types",
      icon: LuFactory,
      iconClass: "explore-subcat-products-hero__stat-icon--cm",
    },
  ];

  return (
    <div className="explore-subcat-products-hero__stats">
      {pills.map((pill) => {
        const Icon = pill.icon;
        return (
          <div key={pill.label} className="explore-subcat-products-hero__stat-pill">
            <div className={`explore-subcat-products-hero__stat-icon ${pill.iconClass}`}>
              <Icon aria-hidden />
            </div>
            <span className="explore-subcat-products-hero__stat-text">
              <strong>{formatStatCount(pill.value)}</strong> {pill.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function ExploreInfraListingsBrowse({
  pageTitle = "Infrastructure Listings",
  pageDescription = "",
  browsePath = {},
  breadcrumbTrail = [],
  listings = [],
  loading = false,
  searchValue = "",
  onSearchChange,
  sort = "newest",
  onSortChange,
  detailTab = "real-estate",
  filterPanelProps = {},
  advertisementBanners = [],
}) {
  const navigate = useNavigate();
  const [localSearch, setLocalSearch] = useState(searchValue || "");
  const [viewMode, setViewMode] = useState("list");

  useEffect(() => {
    setLocalSearch(searchValue || "");
  }, [searchValue]);

  const showingTotal = listings.length;
  const moduleTabs = useMemo(
    () =>
      buildMarketplaceModuleTabs({
        activeKey: "infra",
        counts: {
          products: 0,
          buyerReqs: 0,
          auctions: 0,
          cm: 0,
        },
      }).map((tab) =>
        tab.key === "infra"
          ? {
              ...tab,
              label: `Infrastructure${formatMarketplaceTabCount(showingTotal)}`,
            }
          : tab,
      ),
    [showingTotal],
  );

  const heroStats = useMemo(() => {
    const locations = new Set();
    const transactions = new Set();
    let availableCount = 0;
    listings.forEach((item) => {
      const location = item.locationLine || item.cityName;
      if (location && location !== "—") locations.add(String(location).trim().toLowerCase());
      transactions.add(String(item.transactionType || "lease").toLowerCase());
      if (listingAvailabilityFilterKey(item) === "available") availableCount += 1;
    });
    return {
      listing_count: listings.length,
      location_count: locations.size,
      available_count: availableCount,
      transaction_count: transactions.size,
    };
  }, [listings]);

  const infraBrowseHref = buildMarketplaceInfraBrowseHref(browsePath);
  const typeBrowseHref = browsePath.type
    ? buildMarketplaceInfraBrowseHref({ type: browsePath.type })
    : infraBrowseHref;
  const assetBrowseHref = browsePath.assetCategory
    ? buildMarketplaceInfraBrowseHref({
        type: browsePath.type,
        assetCategory: browsePath.assetCategory,
      })
    : typeBrowseHref;

  return (
    <>
      <section className="explore-subcat-products-hero">
        <div className="explore-subcat-products-hero__inner">
          <button
            type="button"
            className="explore-subcat-products-hero__back"
            onClick={() => navigate(assetBrowseHref)}
          >
            <FaChevronLeft aria-hidden /> Back
          </button>
          <nav className="explore-subcat-products-hero__breadcrumb" aria-label="Breadcrumb">
            <Link to="/home">Home</Link>
            <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
            <Link to="/marketplace/products">Categories</Link>
            <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
            <Link to="/marketplace/products?module=infra">Infrastructure Leasing</Link>
            {breadcrumbTrail.map((part, idx) => {
              const isLast = idx === breadcrumbTrail.length - 1;
              let href = null;
              if (!isLast) {
                if (idx === 0) href = typeBrowseHref;
                else if (idx === 1) href = assetBrowseHref;
                else if (idx === 2) {
                  href = buildMarketplaceInfraBrowseHref({
                    type: browsePath.type,
                    assetCategory: browsePath.assetCategory,
                    subCategory: browsePath.subCategory,
                  });
                }
              }
              return (
                <React.Fragment key={`${part}-${idx}`}>
                  <LuChevronRight className="explore-subcat-products-hero__breadcrumb-sep" aria-hidden />
                  {isLast ? (
                    <span className="is-current">{part}</span>
                  ) : (
                    <Link to={href || infraBrowseHref}>{part}</Link>
                  )}
                </React.Fragment>
              );
            })}
          </nav>

          <div className="explore-subcat-products-hero__main">
            <div className="explore-subcat-products-hero__intro">
              <h1>{pageTitle}</h1>
              <p>{pageDescription || infraListingDescription(pageTitle)}</p>
            </div>
            <InfraListingHeroStats stats={heroStats} />
          </div>
        </div>
      </section>

      <div className="explore-subcat-products-tabs-bar">
        <div className="explore-subcat-products-tabs-bar__inner">
          <nav className="explore-subcat-products-tabs" aria-label="Listing types">
            {moduleTabs.map((tab) =>
              tab.active ? (
                <span key={tab.key} className="explore-subcat-products-tabs__item is-active">
                  {tab.label}
                </span>
              ) : (
                <Link key={tab.key} to={tab.href} className="explore-subcat-products-tabs__item">
                  {tab.label}
                </Link>
              ),
            )}
          </nav>
        </div>
      </div>

      <section className="explore-subcat-products-controls">
        <div className="explore-subcat-products-controls__inner">
          <div className="explore-subcat-products-controls__top-row">
            <form
              className="explore-subcat-products-controls__search"
              onSubmit={(e) => {
                e.preventDefault();
                onSearchChange?.(localSearch);
              }}
            >
              <FaSearch aria-hidden />
              <input
                type="search"
                value={localSearch}
                onChange={(e) => setLocalSearch(e.target.value)}
                placeholder={`Search ${pageTitle.toLowerCase()} listings, location, lease terms…`}
                aria-label={`Search ${pageTitle} listings`}
              />
              {localSearch ? (
                <button
                  type="button"
                  className="explore-subcat-products-controls__clear"
                  aria-label="Clear search"
                  onClick={() => {
                    setLocalSearch("");
                    onSearchChange?.("");
                  }}
                >
                  <FaTimes aria-hidden />
                </button>
              ) : null}
            </form>

            <div className="explore-subcat-products-controls__sort" role="group" aria-label="Sort listings">
              {INFRA_SORT_OPTIONS.map((opt) => {
                const isActive = sort === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`explore-subcat-products-controls__sort-pill${isActive ? " is-active" : ""}`}
                    aria-pressed={isActive}
                    onClick={() => onSortChange?.(opt.value)}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="explore-subcat-products-controls__actions">
            <button
              type="button"
              className="explore-subcat-products-controls__btn explore-subcat-products-controls__btn--orange"
              onClick={() => navigate("/infrastructure-leasing/post-requirement")}
            >
              Post Buying Requirement
            </button>
            <button
              type="button"
              className="explore-subcat-products-controls__btn explore-subcat-products-controls__btn--navy"
              onClick={() => navigate("/infrastructure-leasing/list-asset")}
            >
              List Your Asset
            </button>
            <button
              type="button"
              className="explore-subcat-products-controls__btn explore-subcat-products-controls__btn--blue"
              onClick={() => navigate("/infrastructure-leasing/post-requirement")}
            >
              Post Requirement
            </button>
          </div>
        </div>
      </section>

      <div className="explore-subcat-products-main">
        <InfrastructureListingsFiltersPanel {...filterPanelProps} />

        <section className="explore-subcat-products-content">
          <div className="explore-subcat-products-content__head">
            <p className="explore-subcat-products-content__count">
              {showingTotal > 0
                ? `${showingTotal.toLocaleString()} result${showingTotal === 1 ? "" : "s"} in ${pageTitle} — Listings`
                : `0 results in ${pageTitle} — Listings`}
            </p>
            <div className="explore-subcat-products-view-toggle" aria-label="View mode">
              <button
                type="button"
                className={`explore-subcat-products-view-toggle__btn${viewMode === "list" ? " is-active" : ""}`}
                onClick={() => setViewMode("list")}
                aria-pressed={viewMode === "list"}
                aria-label="Horizontal list view"
                title="Horizontal list view"
              >
                <FaList aria-hidden />
              </button>
              <button
                type="button"
                className={`explore-subcat-products-view-toggle__btn${viewMode === "grid" ? " is-active" : ""}`}
                onClick={() => setViewMode("grid")}
                aria-pressed={viewMode === "grid"}
                aria-label="Vertical grid view"
                title="Vertical grid view"
              >
                <FaTh aria-hidden />
              </button>
            </div>
          </div>

          <div className={`explore-subcat-products-list explore-subcat-products-list--${viewMode}`}>
            {loading && listings.length === 0
              ? Array.from({ length: viewMode === "grid" ? 8 : 6 }).map((_, idx) => (
                  <div
                    key={idx}
                    className={
                      viewMode === "grid"
                        ? "marketplace-product-grid-card marketplace-product-grid-card--skeleton"
                        : "marketplace-product-row marketplace-product-row--skeleton"
                    }
                    aria-hidden
                  />
                ))
              : null}
            {!loading || listings.length
              ? listings.map((item) =>
                  viewMode === "grid" ? (
                    <InfrastructureListingGridCard key={item.id} item={item} detailTab={detailTab} />
                  ) : (
                    <InfrastructureListingListRow key={item.id} item={item} detailTab={detailTab} />
                  ),
                )
              : null}
            {!loading && listings.length === 0 ? (
              <p className="explore-categories-empty">No infrastructure listings match your filters yet.</p>
            ) : null}
          </div>
        </section>
      </div>

      <MarketplaceAdSection
        banners={advertisementBanners}
        ariaLabel="Infrastructure leasing promotional banners"
        hideFallback
      />

      <HubHomeFooter />
    </>
  );
}
